import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RankingsPage() {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await axios.get(`${API}/universities?limit=20`);
        const sorted = response.data.sort((a, b) => a.ranking_global - b.ranking_global);
        setUniversities(sorted);
      } catch (error) {
        console.error('Error fetching rankings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  return (
    <div className="py-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4">Global University Rankings 2026</h1>
          <p className="text-lg text-muted-foreground">
            Comprehensive rankings of the world's top universities based on academic reputation, research output, and student outcomes.
          </p>
        </div>

        {/* Featured Rankings Pages */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Link to="/rankings/top-10-usa-2026">
            <div className="border-2 border-accent rounded-sm p-6 hover:shadow-lg transition-all bg-accent/5">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">\ud83c\uddfa\ud83c\uddf8</span>
                <h2 className="text-2xl font-playfair font-bold">Top 10 Universities in USA (2026)</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Discover the highest-ranked American universities including Harvard, Stanford, MIT, and other Ivy League schools.
              </p>
              <Button size="sm" className="rounded-sm">View Full Rankings \u2192</Button>
            </div>
          </Link>

          <Link to="/rankings/ivy-league-vs-russell-group">
            <div className="border-2 border-primary rounded-sm p-6 hover:shadow-lg transition-all bg-primary/5">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">\ud83c\udf93</span>
                <h2 className="text-2xl font-playfair font-bold">Ivy League vs Russell Group</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Compare America's Ivy League universities with UK's elite Russell Group institutions head-to-head.
              </p>
              <Button size="sm" variant="outline" className="rounded-sm">Compare Now \u2192</Button>
            </div>
          </Link>
        </div>

        {/* Global Rankings List */}
        <div className="mb-8">
          <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">Complete Global Rankings</h2>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 bg-secondary animate-pulse rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="rankings-list">
            {universities.map((uni, idx) => (
              <article key={uni.university_id} className="border border-border/40 bg-card shadow-sm hover:shadow-md transition-all rounded-sm p-6 flex items-center gap-6" data-testid={`ranking-${idx}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-accent text-accent-foreground rounded-sm font-playfair font-bold text-2xl">
                  {uni.ranking_global}
                </div>
                <img src={uni.logo} alt={`${uni.name} logo`} className="w-16 h-16 rounded-sm object-cover" />
                <div className="flex-1">
                  <h3 className="text-xl font-playfair font-bold text-foreground mb-1">{uni.name}</h3>
                  <p className="text-sm text-muted-foreground">{uni.region}, {uni.country}</p>
                </div>
                <div className="hidden md:flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">Acceptance</div>
                    <div className="text-lg font-bold text-foreground">{uni.acceptance_rate}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">National Rank</div>
                    <div className="text-lg font-bold text-foreground">#{uni.ranking_national}</div>
                  </div>
                </div>
                <Link to={`/universities/${uni.university_id}`}>
                  <Button variant="outline" className="rounded-sm" data-testid={`view-details-${uni.university_id}`}>
                    View Details
                  </Button>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}